/**
 * `/sites/:mineCode` — one mine site. Port of the demo's `renderSites(m)`
 * (line 3232).
 *
 * Everything about a place, not just its fleet: who is there, what the ground
 * and the services are doing, how parts and people get in, what is open this
 * morning, the fleet with its condition, and every document in the lake about
 * the site. Seven tabs, the demo's `SITE_TABS`, in the demo's order.
 *
 * ## Data
 *
 * Six domains plus the lake catalogue. They load together rather than per tab:
 * the header alone needs five of them (`siteHead` composes shift, CSS,
 * contract, work-order, technician-job and case contexts), so deferring the
 * rest would buy a page that shows a header and then stalls on the first tab
 * a reader touches.
 *
 * ## Declared reductions against the demo
 *
 * - **Per-technician statistics** on the People tab need `costs.json` (the
 *   timesheets) on top of the six domains; the People tab loads it itself, so
 *   the rest of the page never waits on it, and the figures fill in when it
 *   lands.
 * - **Alert cards** on the Open-work tab are the Issues surface's own
 *   `AlertCard`, rendered read-only from the same `alertCardData()`.
 * - **Site-brief page thumbnail.** The demo renders the brief's base64 WebP
 *   preview; thumbnails live on lake documents, not on the payload-free index,
 *   so the brief is offered as a document to open rather than as an image.
 * - **The locator mini-map** is the shared `MineMap` in its `mini` variant.
 *
 * @see src/versions/v1/modules/sites/MineSiteScene.transform.ts
 * @see docs/logic/sites/mine-site.md
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  InlineError,
  SkeletonBlock,
  SkeletonRows,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { MINES, MINE_ORDER } from '../../data/core';
import type { MineCode } from '../../schema/machine';
import type { LakeIndex } from '../../schema/document';
import { unpackTable } from '../../shared/columnar';
import { useLakeDoc } from '../../shared/lake-drawer';
import {
  dataModeLabel,
  groupNum,
  miningLabel,
  sent1,
  SITE_TABS,
  type SiteTab,
} from '../../shared/oem-engine';
import {
  loadLakeIndex,
  useOemData,
  type CustomerSatisfactionResponse,
  type DailyMetricRow,
  type RosterEntry,
  type ShiftDailyRow,
  type WorkOrder,
} from '../../shared/useOemData';
import { MineMap } from '../../shared/viz/MineMap';
import { useOemDocIdentity } from '../../shared/docs/useOemDocIdentity';
import { MachineCard } from '../fleet/MachineCard';
import { AlertCard } from '../issues/AlertCard';
import { machineCardData, type DailyMetricCtx } from '../../shared/oem-engine';
import { sitesOverview } from './SitesMapScene.transform';
import {
  fleetStrip,
  openJobs,
  siteAlertCards,
  siteBrief,
  siteCompetency,
  siteConditions,
  siteContactRows,
  siteCustomer,
  siteDocGroups,
  siteIncidentCards,
  siteParts,
  siteTechRows,
  siteTechStats,
  swingDate,
  type SiteSceneCtx,
} from './MineSiteScene.transform';

const TAB_ITEMS = SITE_TABS.map(([id, label]) => ({ id, label }));

const isMineCode = (value: string): value is MineCode =>
  (MINE_ORDER as readonly string[]).includes(value);

export function MineSiteScene() {
  const { mineCode = '' } = useParams<{ mineCode: string }>();
  const code = mineCode.toUpperCase();
  const known = isMineCode(code);
  const oem = useOemDocIdentity();

  const navigate = useNavigate();
  const [tab, setTab] = useState<SiteTab>('overview');
  useEffect(() => setTab('overview'), [code]);

  const telemetry = useOemData('telemetry');
  const people = useOemData('people');
  const service = useOemData('service');
  const workOrders = useOemData('work-orders');
  const parts = useOemData('parts');
  const sites = useOemData('sites');

  const [lake, setLake] = useState<LakeIndex | null>(null);
  useEffect(() => {
    let live = true;
    loadLakeIndex().then(
      (index) => live && setLake(index),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);

  const ctx = useMemo<SiteSceneCtx | null>(() => {
    if (!telemetry.data || !people.data || !service.data || !workOrders.data || !parts.data) {
      return null;
    }
    return {
      shiftDaily: unpackTable<ShiftDailyRow>(telemetry.data.shift_daily),
      css: unpackTable<CustomerSatisfactionResponse>(people.data.css),
      roster: unpackTable<RosterEntry>(people.data.roster),
      techs: people.data.tapp_techs,
      contracts: service.data.mx_contracts,
      techJobs: service.data.tapp_jobs,
      cases: service.data.cases,
      training: service.data.lms,
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
      inventory: parts.data.inventory,
      parts: parts.data.parts,
      orders: parts.data.orders,
      lakeEntries: lake?.entries ?? [],
    };
  }, [telemetry.data, people.data, service.data, workOrders.data, parts.data, lake]);

  const dailyCtx = useMemo<DailyMetricCtx | null>(
    () =>
      telemetry.data
        ? { dailyMetric: unpackTable<DailyMetricRow>(telemetry.data.daily_metric) }
        : null,
    [telemetry.data],
  );

  const info = useMemo(
    () => (known && sites.data ? (sites.data.siteinfo[code] ?? null) : null),
    [known, sites.data, code],
  );

  const brief = useMemo(
    () => (known && ctx ? siteBrief(code, ctx, info) : null),
    [known, ctx, code, info],
  );

  const error =
    telemetry.error ?? people.error ?? service.error ?? workOrders.error ?? parts.error ?? sites.error;

  if (!known) {
    return (
      <SurfacePageLayout width="content">
        <SurfacePageBody as="div" className="space-y-6">
          <EmptyState
            icon={<MapPin className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
            title={`No site ${mineCode || 'selected'}`}
            description={`${oem.short} operates six sites: Kalbarri Gold, Broken Ridge, Mt Isolde, Ochre Hill, Gawler Deep and Mt Read.`}
            action={
              <Link
                to="/sites"
                className="text-sm text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
              >
                Back to the site map
              </Link>
            }
          />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const m = MINES[code];

  return (
    <SurfacePageLayout width="full">
      <Commentable name="MineSiteScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-6" as="div">
            <SurfacePageHeader
                title={m.name}
              description={`${m.region}, ${m.state} · ${miningLabel(m)} · ${groupNum(m.depth_m)} m · ${m.commodity} · ${m.ownership}`}
              actions={<HelpAnchor doc="sites/mine-site" />}
            />

            <AskStrip view="overview" />

            <Commentable name="SitePicker">
              <nav aria-label="Switch site" className="flex flex-wrap gap-2">
                {MINE_ORDER.map((other) => (
                  <button
                    key={other}
                    type="button"
                    aria-current={other === code ? 'page' : undefined}
                    onClick={() => navigate(`/sites/${other}`)}
                    className={
                      other === code
                        ? 'rounded-lg border border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)] px-3 py-2 text-left'
                        : 'rounded-lg border border-[var(--glass-border)] px-3 py-2 text-left transition-colors hover:bg-[var(--glass-surface-strong)]'
                    }
                  >
                    <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {MINES[other].name}
                    </span>
                    <span className="block text-[11px] text-muted">
                      {MINES[other].state} · {MINES[other].dc}
                    </span>
                  </button>
                ))}
              </nav>
            </Commentable>

            {error ? (
              <InlineError>{`Site data did not load: ${error.message}`}</InlineError>
            ) : !brief ? (
              <SkeletonRows count={8} />
            ) : (
              <>
                <Commentable name="SiteBrief">
                  <section className="grid grid-cols-1 gap-6 @3xl:grid-cols-[minmax(0,1fr)_minmax(0,220px)]">
                    <div className="space-y-4">
                      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm @xl:grid-cols-3">
                        {brief.head.spec.map(([label, value]) => (
                          <div key={label}>
                            <dt className="text-xs uppercase tracking-wider text-muted">
                              {label}
                            </dt>
                            <dd className="text-zinc-900 dark:text-zinc-100">{value}</dd>
                          </div>
                        ))}
                      </dl>

                      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-border)] @2xl:grid-cols-5">
                        <Stat
                          label="Fleet"
                          value={String(brief.kpis.machines)}
                          detail={`${brief.kpis.battery} battery · ${brief.kpis.autonomous} autonomous`}
                        />
                        <Stat
                          label="Availability"
                          value={brief.kpis.availability != null ? `${brief.kpis.availability}%` : '—'}
                          detail={`${brief.kpis.veryPoorOrWorse} very poor or worse`}
                          colour={brief.kpis.availabilityBand?.colorToken}
                        />
                        <Stat
                          label="Open jobs"
                          value={String(brief.kpis.openJobs)}
                          detail={`${brief.kpis.alerts} alerts · ${brief.kpis.drafts} drafts`}
                        />
                        <Stat
                          label="Satisfaction"
                          value={brief.kpis.css != null ? String(brief.kpis.css) : '—'}
                          detail={`${brief.kpis.cssResponses} responses`}
                        />
                        <Stat
                          label="Supply"
                          value={brief.head.dc}
                          detail={`${brief.head.freightDays} day${brief.head.freightDays > 1 ? 's' : ''} by road`}
                          small
                        />
                      </div>

                      <p className="text-xs text-muted">
                        <span className={`band-pill ${brief.isLive ? 'band-good' : 'band-fair'}`}>
                          {dataModeLabel(m)}
                        </span>{' '}
                        {brief.head.contract ? (
                          <>
                            · <span className="mono">{brief.head.contract.contractnum}</span> ·{' '}
                            {brief.head.contract.type.split(' — ')[0]}
                          </>
                        ) : null}
                        {brief.contractor ? <> · operated by {brief.contractor}</> : null}
                        {' · '}
                        {brief.kpis.maintSuper} (superintendent) · {brief.kpis.planner} (planner)
                      </p>
                    </div>

                    <div className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-3 backdrop-blur-lg">
                      <MineMap
                        mini
                        width={180}
                        selected={code}
                        pins={sitesOverview().map((r) => r.pin)}
                        onSelect={(other) => navigate(`/sites/${other}`)}
                      />
                    </div>
                  </section>
                </Commentable>

                <Commentable name="SiteTabs">
                  <SurfacePageTabs
                    items={TAB_ITEMS}
                    value={tab}
                    onChange={setTab}
                    aria-label="Site detail"
                  />
                </Commentable>

                <div className="space-y-6">
                  {tab === 'overview' ? (
                    <OverviewTab code={code} ctx={ctx!} brief={brief} />
                  ) : null}
                  {tab === 'fleet' ? (
                    <FleetTab code={code} dailyCtx={dailyCtx} ctx={ctx!} />
                  ) : null}
                  {tab === 'people' ? <PeopleTab code={code} ctx={ctx!} info={info} /> : null}
                  {tab === 'parts' ? <PartsTab code={code} ctx={ctx!} info={info} /> : null}
                  {tab === 'conditions' ? <ConditionsTab code={code} info={info} /> : null}
                  {tab === 'work' ? <WorkTab code={code} ctx={ctx!} /> : null}
                  {tab === 'docs' ? <DocsTab code={code} ctx={ctx!} info={info} /> : null}
                </div>
              </>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

function OverviewTab({
  code,
  ctx,
  brief,
}: {
  code: MineCode;
  ctx: SiteSceneCtx;
  brief: ReturnType<typeof siteBrief>;
}) {
  const jobs = useMemo(() => openJobs(code, ctx), [code, ctx]);
  const techs = useMemo(() => siteTechRows(code, ctx), [code, ctx]);
  const oem = useOemDocIdentity();

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Panel title="What makes this site different">
        {brief.info ? (
          <ul className="list-disc space-y-1.5 pl-5 text-sm">
            {brief.info.special_considerations.map((line, i) => (
              <li key={`${i}-${line.slice(0, 20)}`}>{line}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">No site profile loaded.</p>
        )}
      </Panel>

      <Panel title="Ground, services and access" subtitle="First lines only — see the tab for the rest">
        {brief.info ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <Field label="Regime">{brief.info.ground_conditions.regime}</Field>
            <Field label="Ventilation">{sent1(brief.info.mine_services.ventilation)}</Field>
            <Field label="Power">{sent1(brief.info.mine_services.power)}</Field>
            <Field label="Town">{brief.info.access_and_logistics.nearest_town}</Field>
            <Field label="Roster">{brief.info.access_and_logistics.roster}</Field>
          </dl>
        ) : (
          <p className="text-sm text-muted">No site profile loaded.</p>
        )}
      </Panel>

      <Panel title="Open on site this morning" subtitle={`${jobs.length} items`}>
        {jobs.length ? (
          <Table>
            <THead>
              <TR>
                <TH>Item</TH>
                <TH>Machine</TH>
                <TH>What</TH>
                <TH>Waiting on</TH>
              </TR>
            </THead>
            <TBody>
              {jobs.slice(0, 8).map((job, i) => (
                <TR key={`${job.id}#${i}`}>
                  <TD className="mono whitespace-nowrap">
                    {job.id}
                    <span className="block text-[10px] text-muted">{job.kind}</span>
                  </TD>
                  <TD>
                    {job.machine ? (
                      <Link
                        to={`/machines/${job.machine}`}
                        className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                      >
                        {job.machine}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TD>
                  <TD className="max-w-[34ch]">{job.title}</TD>
                  <TD>{job.owner}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <p className="text-sm text-muted">Nothing open on this site.</p>
        )}
      </Panel>

      <Panel
        title="Service team on site"
        subtitle={`${techs.length} ${oem.short} technician${techs.length === 1 ? '' : 's'} based here`}
      >
        {techs.length ? (
          <ul className="space-y-2">
            {techs.map((t) => (
              <li key={t.key} className="flex items-center gap-3">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-accent-600)_18%,transparent)] text-[10px] font-semibold">
                  {t.initials}
                </span>
                <span className="min-w-0 flex-1">
                  <b className="block text-sm text-zinc-900 dark:text-zinc-100">{t.name}</b>
                  <span className="block truncate text-[11px] text-muted">{t.skills}</span>
                </span>
                <span className={`band-pill ${t.onSite ? 'band-good' : 'band-fair'}`}>
                  {t.status}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">
            No {oem.short} technician is based here — jobs are covered from {brief.head.dc} and the
            nearest site.
          </p>
        )}
      </Panel>
    </div>
  );
}

function FleetTab({
  code,
  ctx,
  dailyCtx,
}: {
  code: MineCode;
  ctx: SiteSceneCtx;
  dailyCtx: DailyMetricCtx | null;
}) {
  const groups = useMemo(() => fleetStrip(code), [code]);
  const cards = useMemo(() => {
    if (!dailyCtx) return null;
    return groups.map((g) => ({
      ...g,
      cards: g.machines.map((f) => machineCardData(f, { ...dailyCtx, shiftDaily: ctx.shiftDaily })),
    }));
  }, [groups, dailyCtx, ctx.shiftDaily]);

  if (!cards) return <SkeletonRows count={6} />;

  return (
    <div className="space-y-6">
      {cards.map((group) => (
        <section key={group.key} className="@container space-y-2">
          {group.heading ? (
            <h3 className="text-xs uppercase tracking-wider text-muted">{group.heading}</h3>
          ) : null}
          <div className="grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4">
            {group.cards.map((card) => (
              <MachineCard key={card.machine.machine_id} card={card} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function PeopleTab({
  code,
  ctx,
  info,
}: {
  code: MineCode;
  ctx: SiteSceneCtx;
  info: ReturnType<typeof siteBrief>['info'];
}) {
  const techs = useMemo(() => siteTechRows(code, ctx), [code, ctx]);
  const oem = useOemDocIdentity();
  const contacts = useMemo(
    () => (info ? siteContactRows(code, info, oem.name) : []),
    [code, info, oem.name],
  );
  const customer = useMemo(() => siteCustomer(code), [code]);
  const competency = useMemo(() => siteCompetency(code, ctx), [code, ctx]);

  // Demo line 3254: techStats needs the timesheets, which live in costs.json.
  // Loaded here rather than with the page so no other tab waits on it.
  const costs = useOemData('costs');
  const stats = useMemo(
    () =>
      costs.data
        ? siteTechStats(code, { ...ctx, timesheets: costs.data.timesheets })
        : null,
    [code, ctx, costs.data],
  );
  const m = MINES[code];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Commentable name="SiteKeyContacts">
          <Panel title="Key contacts">
            {contacts.length ? (
              <Table>
                <THead>
                  <TR>
                    <TH>Name</TH>
                    <TH>Role</TH>
                    <TH>Organisation</TH>
                    <TH>Contact</TH>
                  </TR>
                </THead>
                <TBody>
                  {contacts.map((c) => (
                    <TR key={c.key}>
                      <TD>
                        <b className="text-zinc-900 dark:text-zinc-100">{c.name}</b>
                      </TD>
                      <TD>{c.role}</TD>
                      <TD>
                        {c.orgHref ? (
                          <Link
                            to={c.orgHref}
                            className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                          >
                            {c.org}
                          </Link>
                        ) : (
                          c.org
                        )}
                      </TD>
                      <TD className="text-[11px] text-muted">{c.contact}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            ) : (
              <p className="text-sm text-muted">No site profile loaded.</p>
            )}
            <p className="mt-3 text-[11px] text-muted">
              Channel: {customer.channel} ·{' '}
              <Link
                to={customer.href}
                className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
              >
                customer page
              </Link>
            </p>
          </Panel>
        </Commentable>

        <Panel title="Maintenance teams">
          <p className="text-xs uppercase tracking-wider text-muted">
            Site crews — {m.operator_model}
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {m.crews.map((crew) => (
              <li
                key={crew}
                className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-muted dark:bg-zinc-800"
              >
                {crew}
              </li>
            ))}
          </ul>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <Field label="Superintendent">{m.maint_super}</Field>
            <Field label="Planner">{m.planner}</Field>
            <Field label="Reliability">{m.reliability}</Field>
          </dl>

          <p className="mt-4 text-xs uppercase tracking-wider text-muted">
            {oem.short} service team based here
          </p>
          {techs.length ? (
            <ul className="mt-2 space-y-3">
              {techs.map((t) => {
                const st = stats?.[t.name];
                return (
                  <li key={t.key} className="flex items-start gap-3">
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-accent-600)_18%,transparent)] text-[10px] font-semibold">
                      {t.initials}
                    </span>
                    <span className="min-w-0 flex-1">
                      {st ? (
                        <Link
                          to={st.href}
                          className="block text-sm font-semibold text-zinc-900 hover:underline hover:underline-offset-2 dark:text-zinc-100"
                        >
                          {t.name}
                        </Link>
                      ) : (
                        <b className="block text-sm text-zinc-900 dark:text-zinc-100">{t.name}</b>
                      )}
                      <span className="block text-[11px] text-muted">{t.skills}</span>
                      <span className="block text-[11px] text-muted">
                        {t.roster ? (
                          <>
                            {t.roster.Roster} · swing {swingDate(t.roster['Swing start'])} →{' '}
                            {swingDate(t.roster['Swing end'])}
                          </>
                        ) : null}
                        {st ? (
                          <>
                            {t.roster ? ' · ' : null}
                            {st.line}
                            {st.toRenew ? (
                              <span style={{ color: 'var(--crit)' }}>
                                {' · '}
                                {st.toRenew} certificate{st.toRenew > 1 ? 's' : ''} to renew
                              </span>
                            ) : null}
                          </>
                        ) : costs.error ? null : (
                          <SkeletonBlock className="mt-0.5 inline-block h-3 w-40 align-middle" />
                        )}
                      </span>
                    </span>
                    <span className={`band-pill ${t.onSite ? 'band-good' : 'band-fair'}`}>
                      {t.status}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-1 text-sm text-muted">None based here.</p>
          )}
          {costs.error ? (
            <p className="mt-2 text-[11px] text-muted">
              Job and certificate figures did not load: {costs.error.message}
            </p>
          ) : null}

          {info ? (
            <>
              <p className="mt-4 text-xs uppercase tracking-wider text-muted">Cover</p>
              <p className="mt-1 text-sm">
                {info.access_and_logistics.roster}. Nearest workshop support: {m.dc}.
              </p>
            </>
          ) : null}
        </Panel>
      </div>

      <Commentable name="SiteCompetency">
        <Panel title="Competency on site · LMS" subtitle={`${competency.length} records`}>
          {competency.length ? (
            <Table>
              <THead>
                <TR>
                  <TH>Person</TH>
                  <TH>Organisation</TH>
                  <TH>Course</TH>
                  <TH>Expires</TH>
                  <TH>Status</TH>
                </TR>
              </THead>
              <TBody>
                {competency.map((row) => (
                  <TR key={row.key}>
                    <TD>{row.record.person}</TD>
                    <TD className="text-muted">{row.record.org}</TD>
                    <TD className="text-[11.5px]">{row.record.course}</TD>
                    <TD className="mono whitespace-nowrap">{row.record.expires}</TD>
                    <TD>
                      <span className={`band-pill ${row.pill}`}>{row.record.status}</span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <p className="text-sm text-muted">No training records for this site.</p>
          )}
        </Panel>
      </Commentable>
    </div>
  );
}

function PartsTab({
  code,
  ctx,
  info,
}: {
  code: MineCode;
  ctx: SiteSceneCtx;
  info: ReturnType<typeof siteBrief>['info'];
}) {
  const view = useMemo(() => siteParts(code, ctx), [code, ctx]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          label="Site store"
          value={String(view.storeLines)}
          unit="lines held"
          detail={`${view.belowMinimum} below minimum`}
        />
        <Tile
          label="Distribution centre"
          value={view.dc}
          detail={`${view.dcLines} lines · ${view.freightDays} day${view.freightDays > 1 ? 's' : ''} by road`}
          small
        />
        <Tile
          label="Orders in the last 3 weeks"
          value={String(view.orders.length)}
          detail={`${view.draftOrders} drafted · ${view.airFreightOrders} on air freight`}
        />
        <Tile
          label="Predicted need · 30 days"
          value={String(view.predictedGaps)}
          unit="gaps"
          detail={`${view.predicted.length} part lines driven by flagged components here`}
        />
      </div>

      {info ? (
        <Panel title="Getting parts to this site">
          <p className="max-w-[92ch] text-sm">{info.access_and_logistics.freight}</p>
          <p className="mt-2 max-w-[92ch] text-sm">
            <b>Road:</b> {info.access_and_logistics.road}
          </p>
        </Panel>
      ) : null}

      <Panel title="Site store · critical and below-minimum lines">
        <Table>
          <THead>
            <TR>
              <TH>Part</TH>
              <TH>Description</TH>
              <TH className="text-right">On hand</TH>
              <TH className="text-right">Min</TH>
              <TH>Criticality</TH>
            </TR>
          </THead>
          <TBody>
            {view.rows.map((row) => (
              <TR key={row.key}>
                <TD className="mono">{row.line.part_no}</TD>
                <TD className="text-[11.5px]">{row.description}</TD>
                <TD
                  className="mono text-right"
                  style={
                    row.belowMinimum
                      ? { color: 'var(--band-vpoor)', fontWeight: 600 }
                      : undefined
                  }
                >
                  {row.line.qty_on_hand}
                </TD>
                <TD className="mono text-right">{row.line.min_level}</TD>
                <TD>
                  {row.criticality ? (
                    <span
                      className={`band-pill ${row.criticality === 'Critical' ? 'band-vpoor' : 'band-fair'}`}
                    >
                      {row.criticality}
                    </span>
                  ) : null}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Panel>

      <Panel title="Predicted usage against the shelf">
        {view.predicted.length ? (
          <Table>
            <THead>
              <TR>
                <TH>Part</TH>
                <TH>Driven by</TH>
                <TH className="text-right">Need</TH>
                <TH className="text-right">Store</TH>
                <TH className="text-right">DC</TH>
                <TH>Agent proposal</TH>
              </TR>
            </THead>
            <TBody>
              {view.predicted.map((row) => (
                <TR key={`${row.part_no}#${row.mine}`}>
                  <TD className="mono">{row.part_no}</TD>
                  <TD className="text-[11px]">
                    {[...new Set(row.machines)].map((id) => (
                      <Link
                        key={id}
                        to={`/machines/${id}`}
                        className="mr-1 text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                      >
                        {id}
                      </Link>
                    ))}
                  </TD>
                  <TD className="mono text-right">{row.demand}</TD>
                  <TD
                    className="mono text-right"
                    style={row.gap > 0 ? { color: 'var(--band-vpoor)', fontWeight: 600 } : undefined}
                  >
                    {row.store}
                  </TD>
                  <TD className="mono text-right">{row.dcq}</TD>
                  <TD className="text-[11.5px]">{row.prop}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <p className="text-sm text-muted">
            No flagged components driving parts demand here this month.
          </p>
        )}
        {info ? (
          <p className="mt-3 text-[11px] text-muted">Storage on site: {info.facilities.store}</p>
        ) : null}
      </Panel>

      {view.orders.length ? (
        <Panel title="Orders to this site">
          <Table>
            <THead>
              <TR>
                <TH>Order</TH>
                <TH>Date</TH>
                <TH>Part</TH>
                <TH>Machine</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {view.orders.map((order) => (
                <TR key={order.order_no}>
                  <TD className="mono">{order.order_no}</TD>
                  <TD className="mono">{order.order_date}</TD>
                  <TD className="mono">{order.part_no}</TD>
                  <TD>
                    <Link
                      to={`/machines/${order.machine_id}`}
                      className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                    >
                      {order.machine_id}
                    </Link>
                  </TD>
                  <TD className="text-[11.5px]">{order.status}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Panel>
      ) : null}
    </div>
  );
}

function ConditionsTab({
  code,
  info,
}: {
  code: MineCode;
  info: ReturnType<typeof siteBrief>['info'];
}) {
  const blocks = useMemo(() => (info ? siteConditions(code, info) : []), [code, info]);

  if (!info) return <p className="text-sm text-muted">No site profile loaded.</p>;

  return (
    <div className="space-y-6">
      <Panel
        title="Key considerations"
        subtitle="Read these before committing a person or a part"
      >
        <ol className="space-y-2">
          {info.special_considerations.map((line, i) => (
            <li key={`${i}-${line.slice(0, 20)}`} className="flex gap-3 text-sm">
              <span className="mono shrink-0 text-muted">{i + 1}</span>
              <span>{line}</span>
            </li>
          ))}
        </ol>
      </Panel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {blocks.map((block) => (
          <Panel key={block.title} title={block.title}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              {block.pairs.map(([label, value]) => (
                <Field key={label} label={label}>
                  {value}
                </Field>
              ))}
            </dl>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function WorkTab({ code, ctx }: { code: MineCode; ctx: SiteSceneCtx }) {
  const jobs = useMemo(() => openJobs(code, ctx), [code, ctx]);
  const alerts = useMemo(() => siteAlertCards(code, ctx), [code, ctx]);
  const incidents = useMemo(() => siteIncidentCards(code, ctx), [code, ctx]);

  return (
    <div className="space-y-6">
      <Commentable name="SiteOpenAlerts">
        {alerts.length ? (
          <section className="@container space-y-2" aria-labelledby="site-alerts-heading">
            <h3
              id="site-alerts-heading"
              className="text-sm font-semibold text-zinc-900 dark:text-zinc-100"
            >
              Open alerts here
              <span className="ml-2 font-normal text-muted">
                {alerts.length} · with the window and the cost of waiting
              </span>
            </h3>
            <div className="grid grid-cols-1 gap-3 @4xl:grid-cols-2">
              {alerts.map((data) => (
                <AlertCard key={data.alert.id} data={data} />
              ))}
            </div>
          </section>
        ) : (
          <p className="text-sm text-muted">No open alerts on this site.</p>
        )}
      </Commentable>

      {incidents.length ? (
        <Commentable name="SiteIncidents">
          <section className="space-y-2" aria-labelledby="site-incidents-heading">
            <h3
              id="site-incidents-heading"
              className="text-sm font-semibold text-zinc-900 dark:text-zinc-100"
            >
              Incidents
              <span className="ml-2 font-normal text-muted">closed, with the replay</span>
            </h3>
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {incidents.map((inc) => (
                <li key={inc.id}>
                  <Link
                    to={inc.href}
                    className="block h-full rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg transition-colors hover:bg-[var(--glass-surface-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
                  >
                    <span className="flex items-center gap-2">
                      <span className="mono rounded bg-zinc-100 px-1.5 py-px text-[10.5px] text-muted dark:bg-zinc-800">
                        {inc.id}
                      </span>
                      <span className="text-[11px] text-muted">{inc.date}</span>
                    </span>
                    <b className="mt-2 block text-sm text-zinc-900 dark:text-zinc-100">
                      {inc.title}
                    </b>
                    <span className="mt-1 block text-[11.5px] text-muted">{inc.costLine}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </Commentable>
      ) : null}

      <Panel title="Everything open on this site" subtitle={`${jobs.length} items`}>
        {jobs.length ? (
          <Table>
            <THead>
              <TR>
                <TH>Item</TH>
                <TH>Machine</TH>
                <TH>What</TH>
                <TH>Next</TH>
                <TH>Waiting on</TH>
              </TR>
            </THead>
            <TBody>
              {jobs.map((job, i) => (
                <TR key={`${job.id}#${i}`}>
                  <TD className="mono whitespace-nowrap">
                    {job.id}
                    <span className="block text-[10px] text-muted">{job.kind}</span>
                  </TD>
                  <TD>
                    {job.machine ? (
                      <Link
                        to={`/machines/${job.machine}`}
                        className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                      >
                        {job.machine}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TD>
                  <TD className="max-w-[30ch]">{job.title}</TD>
                  <TD className="max-w-[32ch] text-muted">{job.next}</TD>
                  <TD>{job.owner}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <p className="text-sm text-muted">Nothing open.</p>
        )}
      </Panel>
    </div>
  );
}

function DocsTab({
  code,
  ctx,
  info,
}: {
  code: MineCode;
  ctx: SiteSceneCtx;
  info: ReturnType<typeof siteBrief>['info'];
}) {
  const groups = useMemo(() => siteDocGroups(code, ctx), [code, ctx]);
  const openLakeDoc = useLakeDoc();
  const total = groups.reduce((a, g) => a + g.entries.length, 0);

  return (
    <div className="space-y-6">
      {info ? (
        <Panel
          title="Site brief"
          subtitle="What a technician reads before mobilising"
        >
          <p className="text-sm text-muted">
            Access, ground, services, facilities, fleet and the special considerations. Compiled
            from the site profile register and reviewed monthly.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                openLakeDoc({
                  file: info._brief,
                  connector: 'Site profile register',
                  title: `${MINES[code].name} site brief`,
                })
              }
              className="rounded-lg border border-[var(--glass-border)] px-3 py-1.5 text-sm transition-colors hover:bg-[var(--glass-surface-strong)]"
            >
              Open the brief (PDF)
            </button>
            <button
              type="button"
              onClick={() =>
                openLakeDoc({
                  file: info._file,
                  connector: 'Site profile register',
                  title: `${MINES[code].name} site profile`,
                })
              }
              className="rounded-lg border border-[var(--glass-border)] px-3 py-1.5 text-sm transition-colors hover:bg-[var(--glass-surface-strong)]"
            >
              Site profile (JSON)
            </button>
          </div>
        </Panel>
      ) : null}

      <Panel
        title="Everything in the lake about this site"
        subtitle={`${total} files across ${groups.length} systems`}
      >
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.connector}>
              <h4 className="text-xs uppercase tracking-wider text-muted">
                {group.connector} · {group.entries.length}
              </h4>
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {group.entries.slice(0, 10).map((entry) => (
                  <li key={entry.slug}>
                    <button
                      type="button"
                      title={entry.file}
                      onClick={() =>
                        openLakeDoc({ file: entry.file, connector: group.connector })
                      }
                      className="max-w-[34ch] truncate rounded bg-zinc-100 px-2 py-0.5 text-[11px] text-accent-700 hover:underline dark:bg-zinc-800 dark:text-accent-400"
                    >
                      {entry.file}
                    </button>
                  </li>
                ))}
                {group.entries.length > 10 ? (
                  <li className="self-center text-[11px] text-muted">
                    +{group.entries.length - 10}
                  </li>
                ) : null}
              </ul>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

function Panel({
  title,
  subtitle,
  children,
  'data-comment-anchor': commentAnchor,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Set by `<Commentable>` (cloneElement); forwarded to the root element so the anchor reaches the DOM. */
  'data-comment-anchor'?: string;
}) {
  return (
    <section
      data-comment-anchor={commentAnchor}
      className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg"
    >
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        {title}
        {subtitle ? <span className="ml-2 font-normal text-muted">{subtitle}</span> : null}
      </h3>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="whitespace-nowrap text-muted">{label}</dt>
      <dd className="text-zinc-900 dark:text-zinc-100">{children}</dd>
    </>
  );
}

function Stat({
  label,
  value,
  detail,
  colour,
  small,
}: {
  label: string;
  value: string;
  detail: string;
  colour?: string;
  small?: boolean;
}) {
  return (
    <div className="bg-[var(--glass-surface-strong)] px-4 py-3">
      <p className="text-[11px] uppercase tracking-wider text-muted">{label}</p>
      <p
        className={
          small
            ? 'mt-1 text-sm font-semibold text-zinc-900 dark:text-zinc-100'
            : 'mt-1 text-xl font-semibold text-zinc-900 dark:text-zinc-100'
        }
        style={colour ? { color: colour } : undefined}
      >
        {value}
      </p>
      <p className="mt-0.5 text-[11px] text-muted">{detail}</p>
    </div>
  );
}

function Tile({
  label,
  value,
  unit,
  detail,
  small,
}: {
  label: string;
  value: string;
  unit?: string;
  detail: string;
  small?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg">
      <p className="text-xs uppercase tracking-wider text-muted">{label}</p>
      <p
        className={
          small
            ? 'mt-1 text-base font-semibold text-zinc-900 dark:text-zinc-100'
            : 'mt-1 text-2xl font-semibold text-zinc-900 dark:text-zinc-100'
        }
      >
        {value}
        {unit ? <span className="ml-1 text-xs font-normal text-muted">{unit}</span> : null}
      </p>
      <p className="mt-1 text-[11px] text-muted">{detail}</p>
    </div>
  );
}
